import React from "react";
import type { ProfileData, ProfilerNodeTiming } from "@recursive-robot/react-jsx-parser";
import type { CycleEntry } from "../../../store/profiling/types";
import { cycleBatches, batchKey } from "./viz";

/** A selection remembers which cycle (and batch) it belongs to instead of being effect-reset. */
type Selection = { cycleId: string; batchKey: string | null; nodeId: number | null };

/**
 * Batch + node selection for one cycle's detail stack (timeline -> flame graph -> node detail):
 * the active batch is the explicitly selected one, else the render batch, else the first available.
 * The selection is KEYED to its owning cycle/batch rather than reset by effects — when the current
 * cycle doesn't match, the defaults simply apply.  That's what lets the timeline's Template scope
 * select a batch of a *different* cycle (`selectCycle(b.cycleId)` + `selectBatch(b)` in one handler)
 * without the selection being wiped a render later, and it guarantees a stale node id (they are
 * per-batch integers) can never light up in another batch.  Every consumer owns independent state,
 * which also lets the Compare view host two detail stacks side by side.
 */
export function useBatchSelection(cycle: CycleEntry | undefined) {
    const [sel, setSel] = React.useState<Selection | null>(null);

    const batches = React.useMemo(() => (cycle ? cycleBatches(cycle) : []), [cycle]);
    const applied = sel && sel.cycleId === cycle?.cycleId ? sel : null;

    const activeBatch: ProfileData | undefined = React.useMemo(() => {
        if (!batches.length) return undefined;
        return (applied?.batchKey != null ? batches.find(b => batchKey(b) === applied.batchKey) : undefined)
            ?? cycle?.render ?? batches[0];
    }, [batches, applied, cycle]);

    const selectedNode: ProfilerNodeTiming | null = React.useMemo(() => {
        if (!activeBatch || applied?.nodeId == null) return null;
        if (applied.batchKey !== batchKey(activeBatch)) return null;
        return activeBatch.nodes.find(n => n.id === applied.nodeId) ?? null;
    }, [activeBatch, applied]);

    const selectBatch = React.useCallback((b: ProfileData) => {
        setSel({ cycleId: b.cycleId, batchKey: batchKey(b), nodeId: null });
    }, []);

    // Pins the active batch alongside the node, so the id is invalidated with its batch.
    const selectNode = React.useCallback((nodeId: number | null) => {
        if (!cycle || !activeBatch) return;
        setSel({ cycleId: cycle.cycleId, batchKey: batchKey(activeBatch), nodeId });
    }, [cycle, activeBatch]);

    return { activeBatch, selectedNodeId: selectedNode?.id ?? null, selectedNode, selectBatch, selectNode };
}
